/**
 * 全局事件总线 — 用于跨组件/跨页面的事件通信
 *
 * 使用示例：
 *   import { eventsBus } from '@/utils/eventsBus';
 *
 *   // 订阅
 *   const unsubscribe = eventsBus.on('reddot:refresh', () => { ... });
 *
 *   // 发布
 *   eventsBus.emit('reddot:refresh');
 *
 *   // 取消订阅
 *   unsubscribe();
 */

type EventHandler = (...args: any[]) => void;

class EventBus {
  private events: Map<string, Set<EventHandler>> = new Map();

  /** 订阅事件，返回取消订阅函数 */
  on(event: string, handler: EventHandler): () => void {
    if (!this.events.has(event)) {
      this.events.set(event, new Set());
    }
    this.events.get(event)!.add(handler);
    return () => this.off(event, handler);
  }

  /** 取消订阅 */
  off(event: string, handler: EventHandler): void {
    this.events.get(event)?.delete(handler);
  }

  /** 发布事件 */
  emit(event: string, ...args: any[]): void {
    this.events.get(event)?.forEach((handler) => {
      try {
        handler(...args);
      } catch (error) {
        // 静默处理回调异常，避免影响其他订阅者
        console.error(`[eventsBus] Error in handler for "${event}":`, error);
      }
    });
  }

  /** 清除指定事件的所有订阅 */
  clear(event: string): void {
    this.events.delete(event);
  }

  /** 清除所有订阅 */
  clearAll(): void {
    this.events.clear();
  }
}

export const eventsBus = new EventBus();

/** 全局事件名称常量 */
export const Events = {
  /** 审批操作完成（通过/拒绝），红点需要刷新 */
  REDDOT_REFRESH: 'reddot:refresh',
} as const;
